import { Visibility as VisibilityIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SettingEntity } from '@/types/api';

import { useSettings } from '../api/get-settings';

// Modal component for viewing setting value
const ViewSettingModal = ({
  open,
  onClose,
  setting,
}: {
  open: boolean;
  onClose: () => void;
  setting: SettingEntity | null;
}) => {
  const { t } = useTranslation();

  const formatValue = (val: any) => {
    if (val === null || val === undefined) {
      return 'null';
    }

    if (typeof val === 'object') {
      return JSON.stringify(val, null, 2);
    }

    return String(val);
  };

  if (!setting) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('settings.view.title', 'View Setting')}: {setting.key}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('settings.view.value', 'Value')}:
          </Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'grey.300',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '400px',
              overflow: 'auto',
            }}
          >
            {formatValue(setting.value)}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export const SettingsList = () => {
  const { data: settings = [], isLoading, isFetching } = useSettings();
  const { t } = useTranslation();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState<SettingEntity | null>(null);

  const handleViewSetting = (setting: SettingEntity) => {
    setSelectedSetting(setting);
    setViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedSetting(null);
  };

  // Assume settings is an array of settings objects, each with id, key, value, and updatedAt
  // Adjust columns as needed for your actual settings structure
  const columns = useMemo(
    (): GridColDef<SettingEntity>[] => [
      {
        field: 'key',
        headerName: t('settings.list.columns.name', 'Name'),
        valueFormatter: (value: string) => {
          return t(`settings.${value}`, value);
        },
        flex: 1,
      },
      {
        field: 'value',
        headerName: t('settings.list.columns.value', 'Value'),
        flex: 2,
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value;
          const displayValue = JSON.stringify(value);
          return (
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {displayValue}
            </Typography>
          );
        },
      },
      {
        field: 'updatedAt',
        headerName: t('settings.list.columns.updatedAt', 'Updated At'),
        flex: 1,
        valueFormatter: (value: string | number) => {
          if (!value) return '';
          const date = new Date(value);
          return date.toLocaleString();
        },
      },
      {
        field: 'actions',
        headerName: t('settings.list.columns.actions', 'Actions'),
        flex: 0.5,
        // Floor for the single view icon so it stays visible at narrow
        // viewport widths (MT1-1358).
        minWidth: 80,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', width: '100%' }}>
            <Tooltip title={t('settings.view.tooltip', 'View full value')}>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewSetting(params.row);
                }}
                sx={{ p: 0.5 }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* <EditSetting setting={params.row} /> */}
          </Box>
        ),
      },
    ],
    [t],
  );

  return (
    <Stack>
      {settings.length === 0 && !isLoading ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                {t('settings.list.empty', 'No settings found.')}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <DataGrid
          rows={settings}
          getRowId={(row) => row.key}
          columns={columns}
          loading={isLoading || isFetching}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            '--DataGrid-overlayHeight': '300px',
            '& .MuiDataGrid-cell': {
              py: 1,
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      )}

      <ViewSettingModal open={viewModalOpen} onClose={handleCloseModal} setting={selectedSetting} />
    </Stack>
  );
};
